import {Button, FormControl, FormField, openDialog, Separator} from 'platform/components';
import {Box, Heading, HStack, VStack, Text, Space} from 'platform/foundation';
import {Nullish} from 'utility-types';

import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';

import {SeriesResponseBody} from '@dms/api/core';
import {useGetServiceOrderGroupQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {WarehouseDirectSaleVariantsDetailFormSchemaType} from '../../types/WarehouseDirectSaleVariantsDetailFormSchema';
import {AllServiceOrderGroups} from './components/AllServiceOrderGroups';

interface ReportingTabProps extends RequiredTestIdProps {
  control: FormControl<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  formApi: UseFormReturn<WarehouseDirectSaleVariantsDetailFormSchemaType>;
  onDiscard: VoidFunction;
  directSaleVariantId: string | Nullish;
  seriesList: SeriesResponseBody[] | Nullish;
}

export function ReportingTab(props: ReportingTabProps) {
  const {data: orderGroups} = useGetServiceOrderGroupQuery();

  const orderGroupsOptions = orderGroups?.map((group) => ({
    label: group?.name ?? '',
    value: group?.serviceOrderGroupId ?? '',
  }));

  const handleViewAll = () =>
    openDialog(
      <AllServiceOrderGroups data-testid={suffixTestId('allServiceOrderGroups', props)} />,
      {
        title: i18n.t('entity.accounting.lables.allServiceOrderGroups'),
      }
    );

  const excludedServiceOrderGroupIds =
    props.formApi.watch('reporting.excludedServiceOrderGroupIds') ?? [];

  const filteredOrderGroupsOptions =
    orderGroupsOptions?.filter((option) => !excludedServiceOrderGroupIds.includes(option.value)) ??
    [];

  return (
    <>
      <SettingsSection>
        <VStack spacing={4}>
          <HStack justify="space-between" align="center">
            <Heading size={4}>{i18n.t('entity.warehouse.labels.serviceOrderGroups')}</Heading>
            <Button
              title={i18n.t('general.actions.viewAll')}
              variant="link"
              leftIcon="action/visibility"
              onClick={handleViewAll}
              data-testid={suffixTestId('viewAll', props)}
            />
          </HStack>
          <FormField
            control={props.control}
            name="reporting.excludedServiceOrderGroupIds"
            type="multiChoice"
            label={i18n.t('entity.warehouse.labels.excludeServiceOrderGroups')}
            options={orderGroupsOptions ?? []}
            data-testid={suffixTestId('excludedServiceOrderGroups', props)}
          />

          <HStack spacing={4} align="center">
            <Box flex={1}>
              <FormField
                control={props.control}
                name="reporting.defaultServiceOrderGroupId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.defaultServiceOrderGroup')}
                options={filteredOrderGroupsOptions}
                data-testid={suffixTestId('defaultServiceOrderGroup', props)}
              />
            </Box>
            <Box flex={1}>
              <VStack>
                <Space vertical={5} />
                <FormField
                  control={props.control}
                  name="reporting.isDefaultServiceOrderGroupLocked"
                  type="checkbox"
                  label={i18n.t('entity.warehouse.labels.cannotBeChanged')}
                  data-testid={suffixTestId('lockDefaultServiceOrderGroup', props)}
                />
              </VStack>
            </Box>
          </HStack>

          <Separator />
          <VStack spacing={4}>
            <Text color="secondary" size="small">
              {i18n.t('entity.warehouse.labels.activationText')}
            </Text>
            <FormField
              control={props.control}
              name="reporting.isPreDeliveryInspection"
              type="checkbox"
              label={i18n.t('entity.warehouse.labels.preDeliveryInspection')}
              data-testid={suffixTestId('isPreDeliveryInspection', props)}
            />
          </VStack>
        </VStack>
      </SettingsSection>

      <Space vertical={18} />

      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.discard')
              : i18n.t('general.actions.discardChanges'),
            onClick: props.onDiscard,
            'data-testid': suffixTestId('discard', props),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: isNil(props.directSaleVariantId)
              ? i18n.t('general.actions.save')
              : i18n.t('general.actions.saveChanges'),
            'data-testid': suffixTestId('submit', props),
          },
        ]}
      />
    </>
  );
}
